/**
 * PrivacyPolicy Component
 * Privacy Policy page for HR4India / OKR Hub
 */

import React, { useEffect } from 'react';
import { LandingNav, Footer } from '../../components/landing';

export const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Privacy Policy</h1>
          <p className="text-stone-500 mb-8">Last Updated: 08 Jan 2026</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-stone-600 leading-relaxed mb-8">
              This Privacy Policy describes how HR4India, operating the OKR Hub platform ("we", "our", "us"), collects, uses, stores, discloses, and protects information when you access or use the OKR Hub web application, websites, and related services (collectively, the "Services").
            </p>
            <p className="text-stone-600 leading-relaxed mb-8">
              By accessing or using the Services, you acknowledge that you have read, understood, and agree to this Privacy Policy.
            </p>

            <Section title="1. About OKR Hub and HR4India">
              <p>OKR Hub is a business-to-business (B2B) software platform designed to help organizations define, track, review, and manage Objectives and Key Results (OKRs) and related performance data.</p>
              <p>HR4India is the owner and operator of OKR Hub and provides consulting, managed services, and software services related to organizational performance management.</p>
            </Section>

            <Section title="2. Scope of This Privacy Policy">
              <p>This Privacy Policy applies to:</p>
              <ul>
                <li>Visitors to OKR Hub websites</li>
                <li>Registered users of the OKR Hub platform</li>
                <li>Authorized users accessing OKR Hub on behalf of an organization</li>
                <li>Consultants, partners, and administrators using the platform</li>
              </ul>
              <p>This policy does not apply to third-party websites, tools, or services that may be linked or integrated with OKR Hub.</p>
            </Section>

            <Section title="3. Information We Collect">
              <h4 className="font-semibold text-stone-800 mt-4 mb-2">3.1 Information Provided by You</h4>
              <p>We collect information that you or your organization voluntarily provide, including:</p>

              <p className="font-medium text-stone-700 mt-3">a) Account and Identity Information</p>
              <ul>
                <li>Full name</li>
                <li>Business email address</li>
                <li>Organization name</li>
                <li>Role, designation, or department</li>
                <li>Login credentials (stored in encrypted form)</li>
              </ul>

              <p className="font-medium text-stone-700 mt-3">b) Organizational and Performance Data</p>
              <ul>
                <li>Objectives, Key Results, initiatives, milestones</li>
                <li>Team, department, and individual performance data</li>
                <li>Progress updates, metrics, and scores</li>
                <li>Review notes, comments, and feedback</li>
              </ul>

              <p className="font-medium text-stone-700 mt-3">c) Communication Information</p>
              <ul>
                <li>Emails and messages sent to us</li>
                <li>Support requests and queries</li>
                <li>Onboarding inputs, surveys, or feedback</li>
              </ul>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">3.2 Information Collected Automatically</h4>
              <p>When you use OKR Hub, we may automatically collect:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device and operating system details</li>
                <li>Log files, timestamps, and usage activity</li>
                <li>Pages accessed and features used</li>
              </ul>
              <p>This data is used strictly for security, diagnostics, analytics, and service improvement.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">3.3 Cookies and Similar Technologies</h4>
              <p>OKR Hub uses cookies and similar technologies to:</p>
              <ul>
                <li>Maintain secure user sessions</li>
                <li>Remember preferences</li>
                <li>Improve platform performance and usability</li>
                <li>Analyze usage trends</li>
              </ul>
              <p>You may control cookies through your browser settings. Disabling cookies may limit certain features.</p>
            </Section>

            <Section title="4. How We Use Information">
              <p>We use collected information to:</p>
              <ul>
                <li>Provide, operate, and maintain OKR Hub</li>
                <li>Authenticate users and manage access</li>
                <li>Enable OKR creation, tracking, reviews, and reporting</li>
                <li>Communicate service updates, alerts, and support responses</li>
                <li>Improve product features and user experience</li>
                <li>Ensure data security and prevent unauthorized access</li>
                <li>Comply with applicable legal and regulatory obligations</li>
              </ul>
              <p className="font-semibold text-stone-800 mt-4">We do not sell personal data.</p>
            </Section>

            <Section title="5. Data Controller and Data Processor Roles">
              <ul>
                <li>Client organizations using OKR Hub act as <strong>Data Controllers</strong> for their organizational and employee data.</li>
                <li>HR4India / OKR Hub acts as a <strong>Data Processor</strong>, processing data only as instructed by the client organization and for the purpose of providing the Services.</li>
              </ul>
              <p>Where required, a separate Data Processing Agreement (DPA) may govern this relationship.</p>
            </Section>

            <Section title="6. Legal Basis for Processing">
              <p>We process personal data based on one or more of the following legal grounds:</p>
              <ul>
                <li>Performance of a contract</li>
                <li>Legitimate business interests</li>
                <li>Compliance with applicable laws</li>
                <li>Consent, where explicitly obtained</li>
              </ul>
            </Section>

            <Section title="7. Data Sharing and Disclosure">
              <p>We may share information only in the following limited circumstances:</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">7.1 Service Providers</h4>
              <p>With trusted third-party vendors who assist us with:</p>
              <ul>
                <li>Cloud hosting and infrastructure</li>
                <li>System monitoring and analytics</li>
                <li>Customer support and communication tools</li>
              </ul>
              <p>All such providers are contractually bound to protect data and maintain confidentiality.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">7.2 Organizational Access</h4>
              <p>Authorized administrators within your organization may access user and performance data in accordance with internal policies and access controls.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">7.3 Legal and Regulatory Requirements</h4>
              <p>We may disclose information if required to:</p>
              <ul>
                <li>Comply with applicable laws or court orders</li>
                <li>Respond to lawful government requests</li>
                <li>Protect the rights, safety, or property of users or HR4India</li>
                <li>Prevent fraud, misuse, or security incidents</li>
              </ul>
            </Section>

            <Section title="8. Data Retention">
              <p>We retain data only for as long as necessary to:</p>
              <ul>
                <li>Provide the Services</li>
                <li>Meet contractual and legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
              </ul>
              <p>Upon termination of an account or contract, data may be deleted or anonymized, subject to applicable laws and agreed retention periods.</p>
            </Section>

            <Section title="9. Data Security">
              <p>HR4India implements appropriate technical and organizational safeguards, including:</p>
              <ul>
                <li>Encryption of sensitive data</li>
                <li>Role-based access controls</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular system monitoring and security reviews</li>
              </ul>
              <p>While we take reasonable measures to protect data, no system can guarantee absolute security.</p>
            </Section>

            <Section title="10. International Data Transfers">
              <p>Data may be stored or processed on servers located outside India.</p>
              <p>Where such transfers occur, we ensure appropriate safeguards consistent with applicable data protection laws.</p>
            </Section>

            <Section title="11. Your Rights">
              <p>Subject to applicable law, users may have the right to:</p>
              <ul>
                <li>Access their personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion or restriction of data</li>
                <li>Object to certain processing activities</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p>Requests may be submitted using the contact details below.</p>
            </Section>

            <Section title="12. Children's Privacy">
              <p>OKR Hub is intended for business use only and is not designed for individuals under 18 years of age.</p>
              <p>We do not knowingly collect personal data from children.</p>
            </Section>

            <Section title="13. Third-Party Integrations">
              <p>OKR Hub may integrate with third-party tools (such as email, calendar, or HR systems).</p>
              <p>Use of such integrations is governed by the respective third-party privacy policies.</p>
            </Section>

            <Section title="14. Changes to This Privacy Policy">
              <p>We may update this Privacy Policy from time to time.</p>
              <p>Material changes will be communicated through the platform or via email.</p>
              <p>Continued use of the Services after updates constitutes acceptance of the revised policy.</p>
            </Section>

            <Section title="15. Governing Law and Dispute Resolution">
              <p>This Privacy Policy shall be governed by and construed in accordance with the laws of India.</p>
              <p>Any disputes arising out of or in connection with this Privacy Policy or the use of the Services shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra, India.</p>
            </Section>

            <Section title="16. Contact Information">
              <p>For privacy-related questions, concerns, or requests, please contact:</p>
              <div className="bg-stone-50 p-4 rounded-lg mt-4">
                <p className="font-semibold text-stone-800">HR4India</p>
                <p>Email: <a href="mailto:okrhub@hr4india.com" className="text-primary-600 hover:text-primary-700">okrhub@hr4india.com</a></p>
                <p>Address: A502, Dheeraj Ganga, Chincholi Bunder Road, Malad West, Mumbai 400064, MH-India</p>
              </div>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-stone-900 mb-4">{title}</h2>
    <div className="text-stone-600 leading-relaxed space-y-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-1">
      {children}
    </div>
  </section>
);
